import React, { FC, useMemo } from 'react';
import { Heading, Icon, Image, Link, Text, Types } from '@marriott/mi-ui-library';

import { WalletAwardTile } from '../../../../molecules';
import { getNuaTotalCount } from '../../hooks/useWalletAwards';
import { WalletNightlyUpgradeAwardsProps } from '../../types';
import { buildSectionQuantityLabel } from '../../utils/buildSectionQuantityLabel';
import { buildWalletAwardExpirationText } from '../../utils/buildWalletAwardExpirationText';
import { StyledWalletNightlyUpgradeAwardsSection } from './WalletNightlyUpgradeAwardsSection.styles';

type WalletNuaCardViewModel = {
  id: string;
  eyebrow?: string;
  title: string;
  expirationText?: string;
  statusLabel: string;
  statusVariant: 'success' | 'neutral' | 'info';
  statusKind: 'available' | 'approved' | 'requestPending';
  mediaImageSrc?: string;
  mediaImageAltText?: string;
};

/**
 * Nightly Upgrade Awards section (nightlyUpgrade slice). Presentational only;
 * receives the already-fetched NUA items, the shared `common` slice, and the
 * per-type empty state content from the WalletAwardsSection orchestrator.
 */
export const WalletNightlyUpgradeAwardsSection: FC<WalletNightlyUpgradeAwardsProps> = ({
  common,
  nightlyUpgrade,
  emptyState,
  items,
  memberIconColor,
  currentLocale,
  onHowToEarnClick,
  onAwardTileClick,
}) => {
  const cards = useMemo<WalletNuaCardViewModel[]>(() => {
    return items.map(item => {
      const isApplied = item.status !== 'available';
      const statusLabel =
        item.status === 'available'
          ? common?.availableLabel || 'Available'
          : item.status === 'approved'
          ? common?.approvedLabel || 'Approved'
          : common?.requestPendingLabel || 'Request Pending';
      const statusVariant: 'success' | 'neutral' | 'info' =
        item.status === 'available' ? 'success' : item.status === 'requestPending' ? 'info' : 'neutral';

      const quantityLabel = buildSectionQuantityLabel(item.count, nightlyUpgrade?.sectionHeading);

      return {
        id: item.id,
        eyebrow: isApplied ? quantityLabel : undefined,
        title: isApplied ? item.propertyName || quantityLabel : quantityLabel,
        expirationText: isApplied
          ? undefined
          : buildWalletAwardExpirationText(common?.expiresLabel, item.expirationDate, currentLocale),
        statusLabel,
        statusVariant,
        statusKind: item.status,
        mediaImageSrc: isApplied ? item.propertyImageSrc : undefined,
        mediaImageAltText: isApplied ? item.propertyImageAltText : undefined,
      };
    });
  }, [
    items,
    common?.availableLabel,
    common?.approvedLabel,
    common?.requestPendingLabel,
    common?.expiresLabel,
    nightlyUpgrade?.sectionHeading,
    currentLocale,
  ]);

  const totalCount = getNuaTotalCount(items);
  const emptyCtaLabel = emptyState?.nightlyUpgradeEmptyLinkLabel || nightlyUpgrade?.descriptionLinkLabel;
  const emptyCtaLink = emptyState?.nightlyUpgradeEmptyLink || nightlyUpgrade?.descriptionLink;
  const emptyCtaOpenInNewTab =
    emptyState?.nightlyUpgradeEmptyLinkOpenInANewTab ?? nightlyUpgrade?.descriptionLinkOpenInANewTab;
  const emptyImage = emptyState?.nightlyUpgradeEmptyDynamicImage;
  const emptyImageUrl = emptyImage?.assetPath || emptyState?.nightlyUpgradeEmptyImage;
  const emptyImageAlt =
    emptyImage?.altText || emptyState?.nightlyUpgradeEmptyImageAltText || emptyState?.nightlyUpgradeEmptyAltText || '';

  return (
    <StyledWalletNightlyUpgradeAwardsSection
      data-testid="wallet-nightly-upgrade-awards-section"
      data-component-name="o-account-WalletNightlyUpgradeAwardsSection"
    >
      <div className="wallet-nightly-upgrade-awards-section__heading-row">
        <Heading
          variation={Types.headingType.subtitle}
          fontSize={Types.size.large}
          titleText={nightlyUpgrade?.sectionHeading || ''}
          customClass="wallet-nightly-upgrade-awards-section__heading"
        />
        {totalCount > 0 ? <span className="wallet-nightly-upgrade-awards-section__count">{totalCount}</span> : null}
      </div>

      {cards.length > 0 &&
      (nightlyUpgrade?.description ||
        (nightlyUpgrade?.descriptionLinkLabel && (nightlyUpgrade?.descriptionLink || onHowToEarnClick))) ? (
        <p className="wallet-nightly-upgrade-awards-section__description t-font-m m-0">
          {nightlyUpgrade?.description ? <span>{nightlyUpgrade.description} </span> : null}
          {nightlyUpgrade?.descriptionLinkLabel && (nightlyUpgrade?.descriptionLink || onHowToEarnClick) ? (
            onHowToEarnClick ? (
              <Link
                linkClassName="wallet-nightly-upgrade-awards-section__description-link"
                text={nightlyUpgrade.descriptionLinkLabel}
                linkHref="#"
                callback={(event?: React.MouseEvent) => {
                  event?.preventDefault();
                  onHowToEarnClick();
                }}
              />
            ) : (
              <Link
                linkClassName="wallet-nightly-upgrade-awards-section__description-link"
                target={nightlyUpgrade.descriptionLinkOpenInANewTab ? '_blank' : '_self'}
                rel={nightlyUpgrade.descriptionLinkOpenInANewTab ? 'noopener noreferrer' : ''}
                text={nightlyUpgrade.descriptionLinkLabel}
                linkHref={nightlyUpgrade.descriptionLink}
              />
            )
          ) : null}
        </p>
      ) : null}

      {cards.length > 0 ? (
        <div className="wallet-nightly-upgrade-awards-section__tiles">
          {cards.map((card, index) => (
            <WalletAwardTile
              key={card.id}
              eyebrow={card.eyebrow}
              title={card.title}
              expirationText={card.expirationText}
              statusLabel={card.statusLabel}
              statusVariant={card.statusVariant}
              statusIconClass={
                card.statusKind === 'approved' && common?.approvedStatusIcon
                  ? common.approvedStatusIcon
                  : card.statusKind === 'requestPending' && common?.requestPendingStatusIcon
                  ? common.requestPendingStatusIcon
                  : undefined
              }
              mediaIconClass={card.mediaImageSrc ? undefined : nightlyUpgrade?.nightlyUpgradeAwardIcon}
              mediaIconColor={card.mediaImageSrc ? undefined : memberIconColor}
              mediaImageSrc={card.mediaImageSrc}
              mediaImageAltText={card.mediaImageAltText}
              ariaLabel={card.title}
              testId="wallet-nightly-upgrade-award-tile"
              onClick={onAwardTileClick ? () => onAwardTileClick(items[index]) : undefined}
            />
          ))}
        </div>
      ) : emptyState?.nightlyUpgradeEmptyMessage ? (
        <div className="wallet-nightly-upgrade-awards-section__empty-state">
          <div className="wallet-nightly-upgrade-awards-section__empty-media" aria-hidden="true">
            {emptyImageUrl ? (
              <Image
                customClass="wallet-nightly-upgrade-awards-section__empty-image"
                altText={emptyImageAlt}
                defaultImageURL={emptyImageUrl}
                renditions={emptyImage?.renditions as never}
                dynamic={emptyImage?.dynamic}
              />
            ) : nightlyUpgrade?.nightlyUpgradeAwardIcon ? (
              <Icon iconClass={nightlyUpgrade.nightlyUpgradeAwardIcon} isDecorative ariaLabel="" />
            ) : null}
          </div>
          <div className="wallet-nightly-upgrade-awards-section__empty-text">
            {emptyState?.nightlyUpgradeEmptyHeading ? (
              <Text
                element={Types.tags.div}
                fontSize={Types.size.small}
                copyText={emptyState.nightlyUpgradeEmptyHeading}
                customClass="wallet-nightly-upgrade-awards-section__empty-heading t-font-weight-m"
              />
            ) : null}
            <Text
              element={Types.tags.div}
              fontSize={Types.size.small}
              copyText={emptyState.nightlyUpgradeEmptyMessage}
              customClass="wallet-nightly-upgrade-awards-section__empty-message"
            />
          </div>
          {emptyCtaLabel && (emptyCtaLink || onHowToEarnClick) ? (
            onHowToEarnClick ? (
              <Link
                linkClassName="wallet-nightly-upgrade-awards-section__empty-cta"
                text={emptyCtaLabel}
                ariaLabel={emptyCtaLabel}
                linkHref="#"
                callback={(event?: React.MouseEvent) => {
                  event?.preventDefault();
                  onHowToEarnClick();
                }}
              />
            ) : (
              <Link
                linkClassName="wallet-nightly-upgrade-awards-section__empty-cta"
                text={emptyCtaLabel}
                ariaLabel={emptyCtaLabel}
                linkHref={emptyCtaLink}
                target={emptyCtaOpenInNewTab ? '_blank' : '_self'}
              />
            )
          ) : null}
        </div>
      ) : null}
    </StyledWalletNightlyUpgradeAwardsSection>
  );
};
